import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useT } from "../../shared/i18n";
import {
  MAX_PAYLOAD_LENGTH,
  looksLikeLabelAttempt,
  type DisplayPayload,
} from "./payload";

/** Characters shown in the collapsed panel before "Show full content". */
const PREVIEW_CHARS = 320;

interface ScanPayloadViewProps {
  /** A URL, Wi-Fi or text payload, live or recalled from history. */
  payload: DisplayPayload;
}

/**
 * The honest view for a non-label, non-product payload (results-06): it names
 * what was scanned and shows the stored content as inert text. URL, Wi-Fi and
 * text are the only kinds here; a label-shaped payload additionally keeps the
 * unsupported-label explanation, because it was probably meant to be a label
 * and failed plain-UUID validation.
 *
 * Rendering is deliberately text-only: the content is a React text child (never
 * HTML), and no image or link is created from it. Actions live in the sticky CTA
 * bar so copy/open/password are identical for the live result and a history
 * recall. The panel's long-content reveal only ever shows the stored, capped
 * text.
 */
export function ScanPayloadView({ payload }: ScanPayloadViewProps) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  const raw = payload.raw;
  const collapsible = raw.length > PREVIEW_CHARS;
  const shown = collapsible && !expanded ? `${raw.slice(0, PREVIEW_CHARS)}…` : raw;
  const labelAttempt = payload.kind === "text" && looksLikeLabelAttempt(payload.text);

  const kindLabel =
    payload.kind === "url"
      ? t("scanPayloadUrl")
      : payload.kind === "wifi"
        ? t("scanPayloadWifi")
        : t("scanPayloadText");

  return (
    <Box data-testid="scan-payload" data-payload-kind={payload.kind}>
      <Typography
        variant="overline"
        color="text.secondary"
        data-testid="scan-payload-kind"
        sx={{ display: "block", lineHeight: 1.6 }}
      >
        {kindLabel}
      </Typography>

      {payload.kind === "wifi" && (
        <Stack spacing={0.25} sx={{ mb: 1 }} data-testid="scan-payload-wifi">
          <Typography variant="body2" component="p" data-testid="scan-payload-ssid">
            {t("scanPayloadSsid")}: {payload.ssid}
          </Typography>
          <Typography variant="body2" component="p" data-testid="scan-payload-security">
            {t("scanPayloadSecurity")}:{" "}
            {payload.security ? payload.security : t("scanPayloadOpenNetwork")}
          </Typography>
          {payload.hidden && (
            <Typography variant="caption" color="text.secondary" data-testid="scan-payload-hidden">
              {t("scanPayloadHidden")}
            </Typography>
          )}
        </Stack>
      )}

      {labelAttempt && (
        <Alert severity="warning" data-testid="scan-label-attempt" sx={{ mb: 1 }}>
          {t("scanUnsupportedFormat")}
        </Alert>
      )}

      <Box
        component="pre"
        data-testid="scan-payload-content"
        sx={{
          m: 0,
          p: 1,
          bgcolor: "action.hover",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: "0.75rem",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          maxHeight: 220,
          overflow: "auto",
          userSelect: "text",
        }}
      >
        {shown}
      </Box>

      {collapsible && (
        <Button
          size="small"
          variant="text"
          data-testid="scan-payload-expand"
          onClick={() => setExpanded((value) => !value)}
          sx={{ mt: 0.5, px: 0 }}
        >
          {expanded ? t("scanPayloadShowLess") : t("scanPayloadShowFull")}
        </Button>
      )}

      {payload.truncated && (
        <Typography
          variant="caption"
          color="warning.main"
          data-testid="scan-payload-truncated"
          sx={{ display: "block", mt: 0.5 }}
        >
          {t("scanPayloadTruncated", { limit: String(MAX_PAYLOAD_LENGTH) })}
        </Typography>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="scan-payload-text-only"
        sx={{ display: "block", mt: 1 }}
      >
        {t("scanPayloadTextOnly")}
      </Typography>
    </Box>
  );
}
